/// <reference types="vite/client" />

import type { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'
import axios, { isAxiosError } from 'axios'
import * as R from 'remeda'

import { sessionStore } from '@gto/session'

import { getApiBaseUrl } from './base-url'

// Admin v2 API client — separate from `client.ts` because admin auth uses
// opaque DOAT tokens (django-oauth-toolkit), not SimpleJWT. The shared
// `axiosInstance` JWT-decodes the access token in its proactive-refresh
// interceptor and POSTs the refresh to `/v1/token/refresh/` (SimpleJWT) —
// both incompatible with DOAT. Mirrors `apps/affiliates-web/src/api/client.ts`
// (affiliate's standalone DOAT client) but lives in the shared lib so
// `@gto/api-client/admin/react-query` hooks can target it via the orval mutator.
//
// The OAuth client_id is per-app (admin-web has its own DOAT Application row
// on the BE oauth_provider table), so the consumer registers a getter at app
// boot via `registerAdminOAuthClientId`. Calling the client before
// registration is a runtime error — admin requests would fail to refresh
// otherwise, and that failure mode is worth surfacing loudly.

type DoatTokenResponse = {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
  scope: string
}

type ClientIdGetter = () => string

let getOAuthClientId: ClientIdGetter | null = null

export function registerAdminOAuthClientId(getter: ClientIdGetter): void {
  getOAuthClientId = getter
}

/**
 * `error.config` plus the single-retry marker the 401 branch stamps on it. Axios
 * has no built-in "already retried" concept, so the flag rides on the config.
 *
 * It survives the retry because `mergeConfig` copies unrecognised keys onto the
 * config it builds for the new request — not because the object identity is
 * preserved, which it is not. And it cannot leak across unrelated requests
 * because `customInstance` spreads a fresh config object per call.
 */
type RetryableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean }

/**
 * An admin session that refreshing cannot rescue.
 *
 * Distinct from a transport error so callers can tell "try again" from "you are
 * logged out": `apps/admin-web`'s React Query `retry` predicate stops on this,
 * because re-running a request that will 401 again is what multiplied the
 * C-1820 storm by React Query's default three attempts.
 */
export class AdminAuthError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'AdminAuthError'
  }
}

/** Whether `error` is an auth failure that must not be retried. */
export function isAdminAuthFailure(error: unknown): boolean {
  return error instanceof AdminAuthError || (isAxiosError(error) && error.response?.status === 401)
}

type AuthFailureHandler = () => void

let onAuthFailure: AuthFailureHandler | null = null

/**
 * Register what the app does when an admin session becomes unrecoverable.
 *
 * This lib clears the credentials itself but cannot navigate — it has no router
 * and no query client — so `apps/admin-web` registers the two steps only the app
 * can perform (purge the query cache, route to `/login`). That is the same shape
 * as the header's Logout button, which is the reference behaviour — an involuntary
 * logout additionally preserves the deep link so re-authenticating returns the
 * staff member to the page they lost. Without this the session is gone but the
 * page still looks authenticated, which is the "a 401 never logs you out" half
 * of C-1820.
 *
 * Mirrors `registerAdminOAuthClientId`, but deliberately OPTIONAL and silent
 * when unregistered: credentials are cleared before it would run, so a consumer
 * that skips it still lands on `/login` via the route guard on the next
 * navigation. Throwing here would turn a missing redirect into a crash inside
 * an error path.
 */
export function registerAdminAuthFailureHandler(handler: AuthFailureHandler): void {
  onAuthFailure = handler
}

function readClientId(): string {
  if (getOAuthClientId === null) {
    throw new Error(
      '@gto/api-client/admin: OAuth client_id getter is not registered. Call registerAdminOAuthClientId() at app startup before any admin API request.',
    )
  }

  return getOAuthClientId()
}

const baseURL = getApiBaseUrl()

export const adminAxiosInstance = axios.create({
  baseURL,
})

let isRefreshing = false
let failedRequests: { resolve: (value: unknown) => void; reject: (reason?: unknown) => void }[] = []

const processQueue = (error?: Error) => {
  failedRequests.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(true)
    }
  })
  failedRequests = []
}

/**
 * Mint a fresh DOAT access token from the refresh stored in localStorage.
 *
 * Called from two places: (1) `apps/admin-web/src/main.tsx` bootstrap, before
 * the router's first `beforeLoad` runs, so a hard refresh or pasted URL
 * doesn't bounce the staff member to `/login`; (2) the 401-response
 * interceptor on `adminAxiosInstance` for runtime token expiry. Both paths
 * dedupe via the shared `isRefreshing` flag so concurrent callers piggyback
 * on a single POST.
 *
 * Bypasses `adminAxiosInstance` itself so the request interceptor doesn't
 * attach the (expired) Bearer token to the refresh call. The BE Ninja wrapper
 * at `/v4/accounts/oauth2/token/` decodes the body as JSON, mirroring the
 * authorization_code exchange in `apps/admin-web/src/routes/login/oauth/`.
 */
export async function refreshAdminAccessToken(): Promise<string> {
  const refresh = localStorage.getItem('user_refresh')

  const response = await axios.post<DoatTokenResponse>(`${baseURL}/v4/accounts/oauth2/token/`, {
    grant_type: 'refresh_token',
    refresh_token: refresh ?? '',
    client_id: readClientId(),
  })

  const { access_token, refresh_token } = response.data
  sessionStore.getState().setAccessToken(access_token)
  // DOAT rotates refresh tokens by default — the previous one is now
  // invalid. Persist the rotated value so the next 401 has a usable
  // refresh token. The request interceptor reads the new bearer from
  // sessionStore on the next request, so no defaults-headers write needed.
  localStorage.setItem('user_refresh', refresh_token)

  return access_token
}

adminAxiosInstance.interceptors.request.use((request) => {
  const accessToken = sessionStore.getState().accessToken
  const lang = localStorage.getItem('lang') ?? 'en'

  if (R.isNonNullish(accessToken) && !R.isEmpty(accessToken)) {
    request.headers.Authorization = `Bearer ${accessToken}`
  }

  request.headers['Accept-Language'] = lang

  request.paramsSerializer = {
    indexes: null,
  }

  return request
})

const handleAuthFailure = () => {
  // Read before clearing: a live token here means THIS call is the one that ends
  // the session.
  const hadLiveSession = R.isNonNullish(sessionStore.getState().accessToken)

  localStorage.removeItem('user_refresh')
  sessionStore.getState().clearAccessToken()

  // Notify the app once per dead session, not once per failed request. Every
  // concurrently in-flight `/admin/v2/*` request reaches this branch when the
  // post-refresh token is rejected, and running the app handler N times means N
  // cache purges and N redirects for a single logout.
  //
  // Deriving "first" from the state transition rather than a module-level flag
  // keeps this self-resetting: a later sign-in stores a token again, so the next
  // session death notifies again. A flag would need an explicit reset, and
  // missing that reset would silently swallow every subsequent logout.
  if (hadLiveSession) {
    onAuthFailure?.()
  }
}

adminAxiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError | string) => {
    if (!isAxiosError(error)) {
      // The handler's declared type is `AxiosError | string`; in this branch
      // `error` is the string form (axios's non-Axios reject path).
      return Promise.reject(new Error(error))
    }

    const originalRequest: RetryableRequestConfig | undefined = error.config
    if (originalRequest === undefined || error.response?.status !== 401) {
      return Promise.reject(error)
    }

    // A request that already carries a post-refresh token and STILL 401s is not
    // an expiry problem: the token is valid and the permission is missing, so
    // refreshing again cannot change the outcome. Terminate here.
    //
    // This guard is what bounds the retry. The `adminAxiosInstance(...)` calls
    // below re-enter THIS interceptor, and `isRefreshing` is already back to
    // `false` by then, so an unmarked nested 401 took the refresh branch afresh
    // — refresh (200) → retry (401) → refresh (200) → … unbounded. Measured on
    // dev before the fix: ~10 request pairs per second, still climbing after 93
    // (C-1820). The marker is set before the `isRefreshing` split so the queued
    // branch's retry is bounded by the same rule.
    if (originalRequest._retry === true) {
      handleAuthFailure()

      return Promise.reject(
        new AdminAuthError('Admin session rejected after a successful token refresh'),
      )
    }
    originalRequest._retry = true

    if (isRefreshing) {
      try {
        await new Promise((resolve, reject) => {
          failedRequests = [...failedRequests, { resolve, reject }]
        })

        return await adminAxiosInstance(originalRequest)
      } catch (queueError) {
        return Promise.reject(
          queueError instanceof Error ? queueError : new Error(String(queueError)),
        )
      }
    }

    try {
      isRefreshing = true
      await refreshAdminAccessToken()
      isRefreshing = false
      processQueue()
    } catch {
      isRefreshing = false
      // `AdminAuthError` for the queued requests too — each rejects into its own
      // React Query call, whose `retry` predicate needs to recognise this as
      // terminal rather than re-running the request three more times.
      processQueue(new AdminAuthError('Token refresh failed'))
      handleAuthFailure()

      return Promise.reject(new AdminAuthError('Token refresh failed'))
    }

    try {
      return await adminAxiosInstance(originalRequest)
    } catch (retryError) {
      // No 401 special-case here: the retry re-enters this interceptor, so a
      // still-401 response has already been turned into an `AdminAuthError` (and
      // logged out) by the `_retry` guard above. Keeping a second 401 branch
      // would double-fire `handleAuthFailure`, and so double-navigate. Anything
      // reaching this point is a different failure — propagate it unchanged.
      return retryError instanceof Error
        ? Promise.reject(retryError)
        : Promise.reject(new Error('Original request failed after the token was refreshed'))
    }
  },
)

export const customInstance = <T>(config: AxiosRequestConfig): Promise<T> =>
  adminAxiosInstance<T>({ ...config }).then(({ data }) => data)

// oxlint-disable-next-line import/no-default-export -- orval's `override.mutator.path` carries no `name`, so the generated admin endpoint file imports this mutator as a default (KTD5/FE-770). Uses the `oxlint-` prefix, not `eslint-`: this rule is oxlint-only, so an `eslint-` directive makes ESLint fail with "Definition for rule not found".
export default customInstance
